// WHERE THINGS GET CREATED.
//
// Every write in this product was a form sitting in the page it belonged to, competing
// with the reading content around it and pushing everything below it down as it grew.
// The offboarding screen is the worst case: expanding a checklist row moved the page's
// primary action, so the button somebody was reaching for was somewhere else by the
// time they got there.
//
// A create flow belongs in its own surface with its own edges. This is that surface,
// and it is deliberately ONE component rather than a pattern each screen re-implements:
// the dismissal contract below is the part that is easy to get wrong and impossible to
// notice when it is wrong.
//
// # It is a WRAPPER, not a new primitive
//
// A right-anchored Base UI sheet already ships, already carries the focus trap, Escape,
// the focus restore and the portal, and is already used twice. The pane's whole job is
// the three-region shape and the dismissal contract, which is a wrapper's worth of code.
// Nothing was installed to build it.
//
// # MODAL, with a scrim
//
// Carbon's create panel is deliberately non-modal so a person can reference the page
// while filling the form. None of riksu's create flows does: "open a claim", "record a
// leaving", "request leave" are each self-contained. Taking the scrim also makes the
// affordance match the focus trap the primitive installs either way — a panel that
// traps focus while looking dismissible is a panel that reads as broken.
//
// # THE DISMISSAL CONTRACT, which is the whole reason this is one component
//
// Escape, the close button and a click on the scrim all mean the same thing, and all
// three must mean it consistently. With unsaved input they must not simply discard: the
// person typed something, and a surface that throws it away on a stray Escape is a
// surface people stop trusting with anything long.
//
// So `dirty` gates them. Clean, every route out closes immediately — a confirmation
// nobody needs is a confirmation everybody learns to click through. Dirty, all three
// raise the same alert, whose destructive action is named for what it does rather than
// "OK", because a person reading a two-button dialog reads the buttons and not the
// prose.
//
// # A `reading` PANE HAS NOTHING TO SUBMIT, and that is a third arm rather than a fake one
//
// A record's detail pane is a READ. It was reachable with what already existed —
// `onSubmit={close}` and a submit label of "Kapat" — and that produces the exact defect
// the X in the corner was written to fix: two controls four inches apart, both meaning
// close, and a person has to work out which. Worse, `dirty` would then be a claim nobody
// ever set and the discard dialog would be dead code in the one shape most likely to grow
// a form later.
//
// So `reading: true` is its own arm of the submission union. It carries no
// `submitLabel` — there is no verb, which the type now enforces rather than asks — and it
// renders ONE footer button that closes. `dirty` stays false for it by construction: a
// pane that holds nothing typed cannot lose anything, so Escape, the scrim, the X and the
// footer all close it immediately and the discard question never arises. Nobody has to
// wonder whether it will.
//
// # THE FOOTER BUTTON IS OUTSIDE THE FORM, and that broke Enter
//
// The three-region shape puts the primary action in a footer beside the scrolling
// content, so it is not a descendant of whatever `<form>` a screen renders — and a
// button outside a form submits nothing. Measured on the leave pane: somebody typed
// two dates, pressed Enter, and the pane sat there. Nothing looked broken; the form
// simply had no submit button in it.
//
// `formId` fixes it structurally rather than per screen. Given one, the footer button
// becomes a real `type="submit"` associated with that form by id, so Enter in a field
// and a click on the button take THE SAME PATH — which also means a screen cannot wire
// one of the two and forget the other. The prop types make it an either/or: a pane
// either submits through a form or through a callback, never ambiguously both.
//
// The corollary, and the part that is easy to miss: `submitDisabled` stops greying out
// a button and starts being a claim about the form. Enter bypasses a disabled button
// entirely, so any gate that mattered has to ALSO be checked in the submit handler.
//
// Browser Back and router navigation use the shared DiscardGuardScope. A quick-view
// entry can disappear without changing the pathname, so each armed draft records its
// owning record stack. Tab close uses the browser's generic beforeunload question.
//
// # A PANE OVER A PANE, and where the limit lives
//
// A `+ New` inside a picker opens a create flow while a form is already open, so panes
// stack. The MECHANICS of that are the sheet's — the offset, `inert`, the single scrim,
// and Escape landing on the top one are all in `sheet.tsx` and mostly Base UI's. What
// is here is the POLICY, because a limit on how deep somebody can go is a product
// decision and not a rendering one.
//
// Every `Pane` publishes how many panes are open above whatever it contains, and the
// picker reads it. Each useDiscardGuard registers only its unfinished form with the
// router's one scope. A clean parent cannot replace a child's blocker. The latest
// affected draft owns the question; saving it transfers protection to any dirty parent.
//
// A RECORD IS THE SECOND THING THAT STACKS, since 2026-08-09, and for two years' worth of
// references it was the first thing that should have. The product owner opened a person's
// record and pressed the asset under *In their custody*: *"normalde varlık quick view'inin
// üstüne binmesi lazımdı — arka plandaki personnel record quick view'i kayboldu"*. The
// shell's record pane held ONE record, so a reference pressed inside one could only take
// its place — every mechanic on this list was built and the surface with the most
// references in the product had never used any of it. `record-pane.tsx` renders the levels
// nested now and the stack travels on the navigation; nothing in this file changed for it,
// which is the evidence the policy was in the right place. What DID change is the width
// argument below, because a record can now be the pane on top.
//
// # TWO WIDTHS, AND THE DECLARATION IS THE CAUSE RATHER THAN THE SIZE
//
// Arda, twice. First: "quick view tarafında iki farklı boyut olsun biri şu anki bir tane
// de şu ankinin iki katı genişliğinde olsun" — two sizes, the current one and twice it.
// Then, on seeing a five-column table crushed into a centred dialog: "böyle modallar
// kullanmıyoruz her şeyi sidepane'de çözüyoruz tablo olanlarda 2x, normallerde 1x
// boyutunda width olacak şekilde" — riksu does not use modals, everything is solved in
// the side pane, and **the ones with a TABLE are 2x while the normal ones are 1x**.
//
// The second sentence is the important one, because it names a PREDICATE rather than a
// size. So the first prop is `carriesATable` and not `wide`. That is the same move
// `column-kind.tsx` made and for the same reason: a column's alignment comes from what it
// HOLDS, never from a call site choosing `right` — because "should this be right
// aligned?" is a matter of taste that the next screen answers differently, and "is this
// money?" has one answer. Here, "should this pane be wide?" is taste; "does this surface
// carry a table?" is a fact anybody can check by looking at the children, and it is the
// question `every_record_that_holds_a_table_says_so` asks mechanically.
//
// A PDF PREVIEW ADDS NO THIRD WIDTH. It is not a table and therefore remains the normal 1x
// pane; a familiar reader solves legibility with its OWN zoom and continuous scroll rather
// than by widening the product surface. `carriesMedia` names only the other fact the pane
// must know: the child owns the viewport and its scrollbar, so the pane body must not add a
// second one around it.
//
// A BOOLEAN and not a union, because the product decision is that there are exactly two
// widths. `"pane" | "wide"` reads as the first two members of a scale and invites a
// third; there is no resizer and no drag handle for the same reason.
//
// WHERE THE DECLARATION LIVES depends on who knows the answer:
//
//   - A RECORD declares it on its ADDRESS in `app/records.ts`, and the shell's one pane
//     passes it through. A record's body is chosen by `record-pane.tsx` rather than by
//     whoever opened it, so the address is the only place that knows — and a new
//     table-carrying record is one line there and no component.
//   - EVERY OTHER PANE declares it beside the table, at the call site that renders one.
//     That is not a call site picking a size: it is the only code that can see its own
//     children, and the line sits next to the `<DataTable>` that makes it true.
//   - A paged-media preview declares `carriesMedia` beside its viewer so scroll ownership is
//     explicit. It does not participate in the width decision.
//
// A `Flow` deliberately gets nothing. A create flow is a form read down one column and
// `layout.dialog`'s own comment is the argument: a wide form makes the eye travel back
// across the screen between a label and its field. A flow renders fields and never a
// table, so the predicate answers `false` for all thirteen of them by construction — and
// leaving the member off the type is what keeps somebody from reaching for it as a size.
//
// # BOTH ORDERINGS ARE REACHABLE NOW, and one of them loses the ledger
//
// `PANE_LIMIT` is two, so "what does a 2x pane over a 1x one look like" has to be
// answered. THIS SECTION USED TO ANSWER IT WITH "UNREACHABLE", on an argument that was
// true when it was written: nothing that opened a nested pane carried a table, because the
// record pane was rendered once by the shell and was therefore always the pane at depth
// zero, and the second sheet was always a create flow or a correction — fields, so 1x.
//
// A record opened from inside a record is the second sheet now, and a record is the one
// thing here that can be 2x. So the premise is gone rather than the conclusion being
// wrong, and both orderings are RE-MEASURED rather than re-reasoned — a measurement is
// only true of what it measured. Chromium, 1440, against the running stack, walking the
// product owner's own path and then its reverse:
//
//   1x over 2x   /people -> Demir-Yalçın, Zeynep -> VARLIK-1001
//
//                  underneath  left 456  right 1416  width 960  inert
//                  on top      left 960  right 1440  width 480
//                  one scrim, and a 504px strip of the record still showing
//
//                Unchanged from the figures this section already carried, and it reads
//                BETTER than 1x over 1x: at 1x the strip is the 24px `--spacing-underlap`
//                alone, and here half the record stays legible beside the asset it holds.
//
//   2x over 1x   /assets/catalogue/{id} -> the person holding it
//
//                  underneath  left 936  right 1416  width 480  inert
//                  on top      left 480  right 1440  width 960
//                  one scrim, and NO strip: 936..1416 is inside 480..1440
//
//                The parent is covered whole, which is exactly what this section
//                predicted. The ledger — the whole reason the underlap exists — is gone,
//                and with it the only thing on screen saying there is a record underneath.
//
// IT IS LEFT THAT WAY, and the alternative was built in the head and rejected on what it
// costs. Capping the top pane at the width of the one beneath would keep the strip and
// would open a PERSON'S RECORD at 480 — the width `personnel-file.tsx` measured as too
// narrow and dropped a whole column for, and the width the product owner's own rule
// ("tablo olanlarda 2x") exists to refuse. Trading a declared width for 24 pixels of paper
// is the wrong way round, and this file already has the precedent: at 420 the top pane
// covers the one beneath whole and that is recorded as *the design working rather than
// failing*, because covering the screen is what every system does when there is no room
// for a ledger. A 2x pane on a 1440 desktop is that case, arrived at from the other side.
//
// WHAT IT COSTS IS NAMED RATHER THAN HIDDEN: in that one ordering the stack is invisible,
// so Close returns to a record the reader could not see was there. It is the product
// owner's call whether that is worth a narrower person's record, and it is his call
// because both answers are a real loss rather than one being a defect.
//
// The declaration still stays off `Flow` for its own reason, which this does not touch: a
// create flow renders fields and never a table, so the predicate answers `false` for all
// thirteen by construction.

import { XIcon } from "lucide-react";
import {
  createContext,
  type ReactNode,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useT } from "../shell/i18n";
import { useDiscardGuard } from "./discard-guard";
import { Sheet, SheetContent, SheetTitle } from "./sheet";

/**
 * HOW MANY PANES ARE OPEN ABOVE whatever is reading this. Zero on a screen.
 *
 * Nothing outside `Pane` writes it. A screen rendering a pane does not say how deep it
 * is, the same way it does not say where it sits — a number a call site has to get
 * right is a number the next call site gets wrong.
 */
const PaneDepth = createContext(0);

/**
 * HOW DEEP THE PAPER GOES, and it is two.
 *
 * A create flow over the form that needed it is the case that has to work. Three is
 * reachable in riksu today — pick a position, `+ New` position, that pane has a job
 * picker, `+ New` job — and it stops here anyway, for reasons that are not pixels:
 *
 * The DISCARD CONTRACT stops helping. At three the question at the bottom of the stack
 * is about work three removes from what somebody opened the screen to do, and
 * "Yazdıklarınız silinsin mi?" cannot say which of three forms it means.
 *
 * There is always a BETTER DOOR. Every one of these things has a screen —
 * `/setup/jobs`, `/setup/units`, `/people` — reachable from the rail, the palette and
 * the topbar `+`. A stack three deep is a worse version of a place that already exists.
 *
 * And an unbounded stack is a way to lose a form behind four sheets of paper. The limit
 * that is never reached is the limit nobody has to reason about.
 */
export const PANE_LIMIT = 2;

/**
 * HOW DEEP A STACK OF RECORDS GOES, and it is NOT the same number.
 *
 * The limit above is argued from the DISCARD CONTRACT — *"at three the question at the
 * bottom of the stack is about work three removes from what somebody opened the screen to
 * do, and 'Yazdıklarınız silinsin mi?' cannot say which of three forms it means"*. That
 * argument is about FORMS, and it is a good one. A record pane holds no form: there is
 * nothing to discard, so nothing to be ambiguous about.
 *
 * The product owner asked for the difference by describing what he expected and did not
 * get: *"kağıt gibi stacklenmiyor… defter gibi olacaktı orası, 2.'nin üstüne biniyor 3."*
 * The third press was REPLACING the top rather than laying another sheet on it, which is
 * what `PANE_LIMIT` says to do and is right for a form and wrong for reading.
 *
 * THE OFFSET GENERALISES ON ITS OWN, which is why this is a number and not a feature.
 * `record-pane.tsx` renders the levels RECURSIVELY rather than as siblings, precisely
 * because Base UI reads nesting off the React tree — so each parent gets
 * `data-nested-dialog-open` when its child opens and the underlap offset is applied per
 * level by the same rule that already draws the second sheet.
 *
 * FOUR RATHER THAN UNBOUNDED. At 1440 with a 480-wide pane the ledger is 24px a level, so
 * four sheets end 72px apart and the one somebody started from is still visible. An
 * unbounded stack is a way to lose a record behind five sheets of paper, and the bound that
 * is never reached is the bound nobody has to reason about.
 *
 * The FORM limit is untouched: `useCanStack` still answers on `PANE_LIMIT`, so a create
 * flow opened from inside a record still refuses to be the third.
 */
export const RECORD_STACK_LIMIT = 4;

/**
 * A PANE'S CONTENTS DO NOT EXIST UNTIL IT HAS BEEN OPENED.
 *
 * # The defect
 *
 * A pane and the component that holds it are two different things, and only the pane is
 * unmounted while it is shut: Base UI's portal renders nothing at `open={false}`, so a
 * `<Pane>`'s CHILDREN cost nothing — but everything the holder computed ABOVE that
 * `<Pane>` runs anyway. The footer needs `dirty`, `submitDisabled` and `submitting`, so
 * every form in this product keeps its state, its effects and its `useQuotation` calls in
 * the holder rather than in the body, which puts them outside the thing that shuts.
 *
 * Measured against the running stack on 2026-08-07, before this existed: `/expense` asked
 * riksu for `claims?state=draft&…` and `limits?category=…` on every cold load, and
 * `/leave` asked for `leave/cost?…`, for panes nobody had pressed. Same shape and same
 * cause as the seventy-eight requests
 * [`docs/2026-08-07-ninety-requests-to-draw-one-screen.md`](../../docs/2026-08-07-ninety-requests-to-draw-one-screen.md)
 * deleted, and that document already states the rule this makes structural: *a request on
 * every sign-in for a form most people open rarely is a request nobody reads.*
 *
 * # Why it is not `{open ? <Form/> : null}`
 *
 * Because a pane has to survive its own CLOSING. Base UI plays the exit transition on a
 * popup that is still mounted, so a holder torn out the instant `open` goes false takes
 * the panel off the screen and leaves the scrim fading over nothing — which is why every
 * one of these is mounted unconditionally in the first place.
 *
 * So the rule is not "while open", it is ONCE OPENED: nothing before the first press,
 * everything after it, and the answers a pane collected survive re-opening it — which is
 * what `usePrerequisites` in `app/expense-creates.tsx` has always claimed and, until this
 * existed, could never do, because the shell rebuilt the flow from scratch on every open.
 *
 * # SURVIVING IS FOR THE ANSWERS AND NOT FOR THE FORM, which is what `usePaneRaising` is
 *
 * A shell-hosted flow used to be torn out on close, so every open was a fresh mount and
 * nobody had to think about it. Keeping it means two pieces of state that are captured at
 * MOUNT and re-captured at no other moment would now outlive an open: the FIELDS
 * (`record-spend.tsx`'s `useState("")`) and the DIRTY BASELINE beside them
 * (`const [opened] = useState(() => ({ …, on: today() }))`). A form somebody explicitly
 * DISCARDED would come back with their typing in it, and `today()` would freeze at the
 * moment of the first open rather than the current one — the midnight defect
 * `frontend/AGENTS.md` names, made as long as the tab rather than as long as a day, on a
 * field that gets RECORDED as the date a spend happened.
 *
 * So the lifetime is SPLIT rather than extended, and this publishes the seam. A flow's
 * outer wrapper — the one holding the prerequisites — stays mounted; the form inside it is
 * keyed on this counter, so each raise is a fresh mount of the fields and their baseline.
 * That leaves the reset to React rather than asking every flow to remember to reset
 * itself, which is the rule that produced the defect it is fixing.
 *
 * # The state is set during RENDER on purpose
 *
 * An effect would mount the body one frame late, so the sheet would begin its entrance
 * animation empty. React re-renders immediately on a set during render and commits
 * nothing in between, which is exactly the "adjust state when a prop changes" case. It
 * converges because the counter only moves on a false-to-true edge.
 */
export function OnceOpened({
  open,
  children,
}: {
  readonly open: boolean;
  readonly children: ReactNode;
}) {
  const [raised, setRaised] = useState(open ? 1 : 0);
  const [showing, setShowing] = useState(open);
  if (open !== showing) {
    setShowing(open);
    if (open) {
      setRaised((count) => count + 1);
    }
  }
  return raised > 0 ? <PaneRaising.Provider value={raised}>{children}</PaneRaising.Provider> : null;
}

/**
 * HOW MANY TIMES THE PANE ABOVE HAS BEEN RAISED. Zero outside one.
 *
 * A `key` for whatever holds a form's fields, so "each open starts clean" is React's job.
 * A flow that keeps nothing across opens does not need it; one that keeps its
 * PREREQUISITES across opens and its FIELDS for one open is exactly what it is for.
 */
export function usePaneRaising(): number {
  return useContext(PaneRaising);
}

const PaneRaising = createContext(0);

/**
 * A control that makes a pane's lede actionable even when the component that owns it lives
 * in the body.
 *
 * A document record is the forcing case: the read that decides whether a preview exists
 * belongs to the document body, while the product's one pane owns the header. Hoisting the
 * read into the shell would give the document two owners; leaving the control in the body
 * puts the action several sections away from the word it qualifies. The portal replaces
 * the passive lede with the body's control and moves only its presentation. The control and
 * the preview pane stay in the body's React subtree, so a preview opened from a quick view
 * is still a NESTED pane to Base UI.
 *
 * Outside a pane — on a record's full-screen address — the control stays where its record
 * declared it instead of disappearing because no portal target exists.
 */
interface PaneChromeActionPlace {
  readonly formTarget: HTMLElement | null;
  readonly target: HTMLElement | null;
  readonly claim: (claimed: boolean) => void;
}

const PaneHeaderLedeActionTarget = createContext<PaneChromeActionPlace | undefined>(undefined);

export function PaneHeaderLedeAction({ children }: { readonly children: ReactNode }) {
  const place = useContext(PaneHeaderLedeActionTarget);
  const claim = place?.claim;
  useLayoutEffect(() => {
    if (!claim) return;
    claim(true);
    return () => claim(false);
  }, [claim]);

  // A `null` target means a pane owns the action but its header has not committed yet. Drawing the
  // control in the body for that first frame would make it visibly jump into the header.
  // No place is the genuinely unowned, full-screen case and keeps it in place.
  return !place
    ? children
    : place.target
      ? createPortal(
          <span data-slot="pane-header-lede-action-content">{children}</span>,
          place.target,
        )
      : null;
}

// A contents-only footer target has no geometry when empty, so it needs no header-style claim.
// React portals keep body-owned actions at their original depth and preserve nested focus.
export function PaneFormActions({ children }: { readonly children: ReactNode }) {
  const target = useContext(PaneHeaderLedeActionTarget)?.formTarget;
  return target ? createPortal(children, target) : null;
}

/** How many panes are open above the caller. */
export function usePaneDepth(): number {
  return useContext(PaneDepth);
}

/**
 * Whether one more pane may be opened from here.
 *
 * A control that may not opens NOTHING rather than opening a pane that would be the
 * third: the affordance is not rendered at all, which is the rule `create-menu.tsx`
 * follows when it renders no button rather than an empty menu. A disabled control and
 * a control that quietly does nothing are both a control that lied.
 */
export function useCanStack(): boolean {
  return useContext(PaneDepth) < PANE_LIMIT;
}

export interface PaneProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** The heading, and the accessible name of the dialog. */
  readonly title: string;
  /** A second, interactive pane beside this one on wide screens. */
  readonly companion?: "chat" | "form";
  /**
   * One sentence under the title saying what this creates. Optional.
   *
   * THERE IS DELIBERATELY NO `why` HERE, and it was built and removed rather than never
   * tried. `Page` and `Section` both took one — the `Explains` trigger beside the heading,
   * the sentence on an always-mounted `sr-only` span — and on a pane it MOVES THE INITIAL
   * FOCUS. Measured, in `pane.test.tsx`'s own harness: a pane with no explanation opens
   * with focus on the close button, and adding one puts the trigger ahead of it in the
   * header, so the pane opens with focus on an info glyph and Base UI's `useFocus` pops
   * the tooltip on arrival. Fixing that means passing `initialFocus` and deciding where
   * EVERY pane starts, which is a product decision and not this one's to make.
   *
   * The bar was already highest here for a different reason and the two agree: a pane's
   * lede is read at the moment somebody has decided to act, not on arrival at a screen, so
   * of sixteen pane ledes surveyed, fourteen said something the reader acts on — what the
   * submit will do, which form this is, what riksu will refuse. A surface where almost
   * nothing qualifies is not a surface that needs the mechanism.
   */
  readonly lede?: string;
  /**
   * Contextual actions for a reading pane's subject. They sit beside the close control,
   * which is the same upper-right action zone a full page exposes through `Page.actions`.
   */
  readonly headerActions?: ReactNode;
  /** Secondary actions beside the primary submit, using the same form association. */
  readonly formActions?: ReactNode;
  /**
   * THIS PANE'S BODY CONTAINS A TABLE, so it opens at 2x. Arda's rule, in his terms.
   *
   * NOT "make this one wide". The width is derived — `--frame-pane` is 1x and
   * `--frame-pane-wide` is 2x and there is no third — and what is declared is the FACT
   * that decides it, so the question at a call site is "does this render a `<DataTable>`"
   * rather than "how big should this be". The first has one answer and the second is
   * taste; `column-kind.tsx` settled the same argument for a cell's alignment.
   *
   * A record does not set this at a call site at all: it declares it on its address in
   * `app/records.ts` and the shell's one pane passes it here. Everything else declares it
   * beside the table it renders, which is the only code that can see its own children.
   */
  readonly carriesATable?: boolean;
  /**
   * This pane carries paged or full-frame media that owns its own viewport and scrollbar.
   *
   * This changes only body overflow. Media is not a table, so the pane keeps the normal 1x
   * width and the reader's own zoom preserves legibility.
   */
  readonly carriesMedia?: boolean;
  /** A document and its decision record are reviewed alongside each other. */
  readonly comparesDocument?: true;
  /**
   * Whether anything has been typed.
   *
   * Not "has anything been touched": a person who opened the pane, clicked a field and
   * changed nothing has nothing to lose, and confirming there teaches them to dismiss
   * the confirmation without reading it.
   */
  readonly dirty?: boolean;
  /** Query keys whose change removes this form's owner; other query edits retain it. */
  readonly ownerQuery?: readonly string[];
  /** True while the write is in flight. Disables the primary action and says so. */
  readonly submitting?: boolean;
  /**
   * Set when the form is not yet answerable. The action says why through the form.
   *
   * With `formId` this greys the button and NOTHING ELSE: Enter in a field goes
   * straight to the form's own handler, which is where the gate has to be as well.
   */
  readonly submitDisabled?: boolean;
  /**
   * WHERE FOCUS GOES when this pane closes, when it is not the thing that opened it.
   *
   * Base UI restores focus to the dialog's TRIGGER, which is right for a pane opened by
   * a button and wrong for one opened from inside a list that no longer exists: a
   * `+ New` in a combobox is destroyed the moment the list closes, so focus lands on
   * `<body>` and a keyboard reader is dropped at the top of the document with a form
   * still open behind them.
   *
   * The combobox hands its input over here. It is also where the person needs to be —
   * the value they just created is now selected in it.
   */
  readonly returnFocusTo?: React.RefObject<HTMLElement | null>;
  readonly children: React.ReactNode;
}

/**
 * How this pane's primary action reaches the write, or that there is no write.
 *
 * A union rather than optional props, because "neither" and "both" are each a pane whose
 * button does something surprising, and the type is the cheapest place to rule them out.
 * The `submitLabel` travels with the two that submit for the same reason: a read pane has
 * no verb, and a type that still asked for one would be asking somebody to invent it.
 */
export type PaneSubmission =
  | {
      /**
       * The `id` of a `<form>` in `children`. The footer button submits it, so Enter
       * in a field and a click take the same path.
       */
      readonly formId: string;
      /** The primary action's label — "Talep et", "Kaydet". Never "OK". */
      readonly submitLabel: string;
      readonly onSubmit?: never;
      readonly reading?: never;
      readonly conversation?: never;
    }
  | {
      readonly formId?: never;
      readonly submitLabel: string;
      /** For a pane whose content is not a form — a confirmation, a chooser. */
      readonly onSubmit: () => void;
      readonly reading?: never;
      readonly conversation?: never;
    }
  | {
      readonly formId?: never;
      readonly submitLabel?: never;
      readonly onSubmit?: never;
      /**
       * NOTHING IS BEING WRITTEN. A record's detail is a read.
       *
       * The footer holds one button and it closes; there is no second control four inches
       * from the X saying the same thing in different words. `dirty` cannot be true here —
       * see the note at the top of this file.
       */
      readonly reading: true;
      readonly conversation?: never;
    }
  | {
      readonly formId?: never;
      readonly submitLabel?: never;
      readonly onSubmit?: never;
      readonly reading?: never;
      /** A conversation owns its composer and needs the full middle region. */
      readonly conversation: true;
    };

export function Pane({
  formId,
  open,
  onOpenChange,
  title,
  companion,
  lede,
  headerActions,
  formActions,
  carriesATable = false,
  carriesMedia = false,
  comparesDocument,
  dirty = false,
  ownerQuery,
  submitLabel,
  onSubmit,
  // NO DEFAULT. The union types it `true | undefined` and `= false` widens it back, which
  // is the type saying the third arm is not one of the two others rather than a value
  // needing a fallback. Absent is falsy everywhere it is read below.
  reading,
  conversation,
  submitting = false,
  submitDisabled = false,
  returnFocusTo,
  children,
}: PaneProps & PaneSubmission) {
  const t = useT();
  const [confirming, setConfirming] = useState(false);
  const [headerLedeActionTarget, setHeaderLedeActionTarget] = useState<HTMLElement | null>(null);
  const [headerLedeClaimed, setHeaderLedeClaimed] = useState(false);
  const [formActionsTarget, setFormActionsTarget] = useState<HTMLElement | null>(null);
  const headerLedeActionPlace = useMemo(
    () => ({
      target: headerLedeActionTarget,
      claim: setHeaderLedeClaimed,
      formTarget: formActionsTarget,
    }),
    [headerLedeActionTarget, formActionsTarget],
  );
  // ONE DEEPER FOR EVERYTHING INSIDE. Read here rather than assumed, so a pane opened
  // from inside another pane knows it is the second and not the first.
  const depth = useContext(PaneDepth);
  const below = depth + 1;
  // THE OTHER THREE EXITS. Base UI gives this component Escape, the close button and the
  // scrim; a rail link, a programmatic navigation and closing the tab were losing the
  // typing silently, and nothing on screen looked wrong afterwards. One hook holds all
  // three, and it renders the SAME dialog as the pane's own routes rather than a second
  // one worded differently.
  //
  // `reading` never arms it: a read pane holds nothing typed, so there is nothing a
  // navigation could lose. Said in the condition rather than left to every caller to
  // remember not to pass `dirty` — and a record pane is itself reached BY navigating, so
  // an armed guard here would ask whether to discard a page.
  const leaving = useDiscardGuard(open && dirty && !reading && !conversation, ownerQuery);

  /**
   * Every route out, through one decision.
   *
   * Base UI calls this for Escape, for the close button and for a scrim click, so the
   * three cannot disagree — which is exactly what happens when each is wired
   * separately and one of them is forgotten.
   */
  function requestClose(next: boolean) {
    if (next) {
      onOpenChange(true);
      return;
    }
    if (dirty && !reading && !conversation) {
      setConfirming(true);
      return;
    }
    onOpenChange(false);
  }

  function discard() {
    setConfirming(false);
    onOpenChange(false);
    // AND LET THE NAVIGATION HAPPEN, if one is what raised the question. Closing the
    // pane and leaving somebody where they were would answer a question they did not
    // ask -- they pressed a link.
    if (leaving.asking) {
      leaving.discard();
    }
  }

  function keep() {
    setConfirming(false);
    if (leaving.asking) {
      leaving.keep();
    }
  }

  return (
    <>
      <Sheet open={open} onOpenChange={requestClose} modal={companion ? false : undefined}>
        <SheetContent
          side="right"
          showCloseButton={false}
          showBackdrop={!companion}
          // THE DESKTOP AND THE PHONE ARE TWO DECISIONS, and only one of them had been
          // made. The width is a token, and the modifier set MATTERS: the generated sheet
          // ships `data-[side=right]:sm:max-w-sm`, whose attribute selector outranks a
          // plain `sm:max-w-*`. A width written without the modifier loses silently and
          // the pane stays 384px — which is narrower than the 448px forms it holds.
          //
          // `w-full` is the phone's half of it. The primitive's default is `w-3/4`, so at
          // 420px the pane was 315 wide with 105 pixels of scrim beside it — a strip too
          // narrow to read the page through and wide enough to lose a fact off the right
          // of a `<dl>`. A panel that covers a phone whole is what every system does at
          // this width, and it costs the desktop nothing: above `sm` the max-width still
          // decides, because three quarters of any screen this product runs on is wider
          // than `--frame-pane` anyway.
          //
          // AND THE STACK HAS NO LEDGER AT THIS WIDTH. Measured on the integrated branch:
          //
          //   420   underneath { left: -24, width: 420 }   top { left: 0, width: 420 }
          //   1440  underneath { left: 936, width: 480 }   top { left: 960, width: 480 }
          //
          // At 420 the sheet underneath spans -24 to 396 and the top one 0 to 420, so the
          // offset goes off the left edge of the viewport and the one underneath is
          // entirely covered. The paper-stack reading exists on a desktop and not on a
          // phone.
          //
          // That is the design working rather than failing — covering a phone whole is
          // what every system does at this width, and a 24px ledger on a 420px screen was
          // always marginal. What was wrong was this comment: it claimed the stack read
          // BETTER at 420, which was TRUE when it was written and measured, at `w-3/4`,
          // where the panes sat at 81 and 105 and the strip really was bounded by the
          // dimmed page. `w-full` moved the premise and the conclusion was carried across
          // it — by me, resolving a merge between the two changes.
          //
          // A measurement is only true of what it measured. Re-measured rather than
          // re-reasoned, by the session that took the original numbers.
          //
          // TWO WIDTHS, WRITTEN IN THE SAME MODIFIER SHAPE. Both branches carry the
          // `data-[side=right]:` prefix and both are whole literals rather than one
          // string with the token interpolated into it — the first because the trap
          // above eats a plain `sm:max-w-*` silently, and the second because Tailwind
          // finds a class by SCANNING this file, so a name assembled at runtime
          // generates no rule and the pane falls back to the primitive's `sm:max-w-sm`.
          // Both failures are 384px of pane and nothing red anywhere.
          //
          // `w-full` is shared, and it is what makes 2x safe on a small screen: the
          // element is `fixed` against the viewport, so its width is
          // `min(100%, max-width)` and a 960px ceiling can never be wider than the
          // window. Measured in Chromium — at 420 both sizes render 420 and the
          // computed `max-width` is `none`, because `sm:` does not apply below 640.
          className={cn(
            "w-full",
            companion === "chat" && "lg:right-(--frame-pane)",
            companion === "form" && "pane-companion-form",
            carriesATable || comparesDocument
              ? "data-[side=right]:sm:max-w-(--frame-pane-wide)"
              : "data-[side=right]:sm:max-w-(--frame-pane)",
          )}
          // A REF OR NOTHING. `finalFocus` also takes `false` and a callback; leaving it
          // `undefined` keeps Base UI's own restore-to-the-trigger, which is the right
          // behaviour for every pane that HAS a trigger. It is passed when the trigger
          // may be GONE by the time the pane closes — a pane opened from a list row that
          // the write removed would otherwise return focus to `<body>`.
          finalFocus={returnFocusTo}
        >
          {/* THREE REGIONS, and only the middle one scrolls. A footer that scrolls away
              is a primary action somebody has to hunt for, and a header that scrolls
              away is a person who has lost track of what they are filling in. */}
          <header className="flex shrink-0 flex-col gap-1 border-b border-border px-6 py-4">
            <div className="flex items-start justify-between gap-4">
              <SheetTitle className="m-0 min-w-0 flex-1 text-[length:var(--type-shell-surface-title-size)] leading-[var(--type-shell-surface-title-leading)] font-medium">
                {title}
              </SheetTitle>
              {/* AN X, NOT A SECOND "CANCEL".
               *
               * This corner and the footer both said `pane.cancel`, in the same
               * words, four inches apart. Two controls with identical labels read as
               * two different actions — somebody who has typed into the form has to
               * work out which one discards it, and the honest answer was neither,
               * because they were the same call.
               *
               * They stay two controls, because they answer at two moments: the
               * corner is "I opened this by mistake" before reading anything, the
               * footer is "I have read it and I am not doing it". Different
               * presentations say that; two identical labels said nothing.
               *
               * The route out is unchanged — the same one Escape and the scrim take,
               * because three exits that behave differently is three things to
               * learn. */}
              <div className="flex shrink-0 items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => requestClose(false)}
                  aria-label={t("pane.close")}
                >
                  <XIcon aria-hidden="true" />
                </Button>
              </div>
            </div>
            {lede ? (
              <div className="flex min-w-0 items-center gap-1">
                {headerLedeClaimed ? null : (
                  <p className="m-0 min-w-0 text-shell-body text-muted-foreground">{lede}</p>
                )}
                <span
                  ref={setHeaderLedeActionTarget}
                  className="flex shrink-0 items-center"
                  data-slot="pane-header-lede-action"
                />
              </div>
            ) : null}
            {headerActions ? (
              <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
                {headerActions}
              </div>
            ) : null}
          </header>

          {/* EVERYTHING INSIDE IS ONE DEEPER, which is what a picker in here reads to
              decide whether it may still offer a `+ New`. Around the body only: the
              header and the footer are this pane's own chrome and open nothing.

              `relative` on the same rule `main` carries, and this is the region a quick
              view scrolls in. Without it the containing block for anything out of flow in
              here — a `sr-only` caption on a record's table, a live region — is the
              POPUP, which is `fixed` and draws no scrollbar, so such an element would sit
              wherever the body happened to have scrolled to and hang out of the panel. */}
          <div
            className={cn(
              "relative min-h-0 flex-1",
              conversation ? "overflow-hidden" : "px-6 py-4",
              carriesMedia ? "overflow-hidden" : !conversation && "overflow-y-auto",
            )}
          >
            <PaneHeaderLedeActionTarget.Provider value={headerLedeActionPlace}>
              <PaneDepth.Provider value={below}>{children}</PaneDepth.Provider>
            </PaneHeaderLedeActionTarget.Provider>
          </div>

          {conversation ? null : (
            <footer className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border px-6 py-4">
              {/* READING: ONE BUTTON, and it is the only thing in the footer. A "Cancel"
                beside a "Close" is the two-identical-controls defect the corner X was
                written to fix, and there is no verb to put on a primary — nothing is being
                done to anything. It stays a full-height control rather than only the X in
                the corner, because on a phone the bottom of the panel is where the thumb
                is and a 32px glyph at the top right is not. */}
              {reading ? (
                <Button onClick={() => requestClose(false)}>{t("pane.done")}</Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => requestClose(false)}>
                    {t("pane.cancel")}
                  </Button>
                  {formActions}
                  <span ref={setFormActionsTarget} className="contents" />
                  {/* The primary is LAST, which is the order every system riksu follows uses
                    for a right-to-left reading of a decision: the thing you are most likely
                    to want sits closest to where the eye finishes. */}
                  {/* ASSOCIATED BY ID when there is a form, so this button is that form's
                    submit rather than a click handler that happens to do the same thing.
                    Without it Enter in a field reaches nothing at all. */}
                  <Button
                    data-form-primary
                    type={formId ? "submit" : "button"}
                    form={formId}
                    onClick={formId ? undefined : onSubmit}
                    disabled={submitting || submitDisabled}
                  >
                    {submitting ? t("pane.submitting") : submitLabel}
                  </Button>
                </>
              )}
            </footer>
          )}
        </SheetContent>
      </Sheet>

      {/* ON TOP of the pane rather than replacing it, so the thing being discarded is
          still visible behind the question about discarding it. */}
      <AlertDialog
        open={confirming || leaving.asking}
        onOpenChange={(next) => (next ? setConfirming(true) : keep())}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("pane.discardTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("pane.discardBody")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {/* KEEPING is the safe answer and gets the quiet button; discarding is
                named for what it does. "OK" on a two-button dialog is a coin toss,
                because people read the buttons and not the sentence above them. */}
            <AlertDialogCancel>{t("pane.keepEditing")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={discard}
              className={cn("bg-destructive text-destructive-foreground")}
            >
              {t("pane.discard")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
